<template>
  <div class="page page--fill stats">
    <div class="page-header">
      <div>
        <h1>Statistiques</h1>
        <p>Calculées en direct sur l'API {{ $env.label }}, hors ventes dont le programme a été supprimé (environ 2 200 dossiers de 2022).</p>
      </div>
    </div>

    <div class="filters">
      <div class="segmented">
        <button
          v-for="p in periods"
          :key="p.key"
          type="button"
          :class="{ 'is-active': period === p.key }"
          @click="setPeriod(p.key)">
          {{ p.label }}
        </button>
      </div>
      <remote-select
        v-model="promoteur"
        :resource="accountResource"
        label="Promoteur"
        style="max-width: 260px"
        @input="fetch" />
      <remote-select
        v-model="account"
        :resource="accountResource"
        label="Compte vendeur"
        style="max-width: 260px"
        @input="fetch" />
    </div>

    <div class="kpis">
      <router-link
        v-for="status in cards"
        :key="status.value"
        :to="{ name: 'reservations', query: listQuery(status.value) }"
        class="kpi">
        <span :class="pillClass(RESERVATION_STATUS, status.value)">{{ cardLabel(status) }}</span>
        <strong>{{ format(counts[status.value]) }}</strong>
        <span class="kpi__basis">{{ basisLabel(status.value) }}</span>
      </router-link>
    </div>

    <div class="charts">
      <div
        v-for="serie in series"
        :key="serie.key"
        class="panel">
        <div class="panel__title">
          {{ serie.title }}
        </div>
        <div class="panel__body">
          <div class="chart">
            <div
              v-for="month in serie.months"
              :key="month.key"
              class="chart__col"
              :title="`${month.label} : ${format(month.count)} ${serie.unit}`">
              <span class="chart__value">{{ month.count === undefined ? '' : month.count }}</span>
              <div class="chart__track">
                <div
                  class="chart__bar"
                  :style="{ height: barHeight(serie.months, month.count) }" />
              </div>
              <span class="chart__label">{{ month.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="panel">
        <div class="panel__title">
          Top 5 promoteurs (ventes actées, {{ periodLabel }})
        </div>
        <div class="panel__body">
          <div
            v-if="topPromoteurs === null"
            class="cell-muted">
            Chargement…
          </div>
          <div
            v-else-if="!topPromoteurs.length"
            class="cell-muted">
            Aucune vente actée sur la période.
          </div>
          <router-link
            v-for="item in topPromoteurs"
            :key="item.pk"
            :to="{ name: 'reservations', query: { status: 'sold', promoteur: item.pk } }"
            class="hbar">
            <span class="hbar__name">{{ item.name }}</span>
            <div class="rate__bar">
              <div :style="{ width: ratio(item.count, topPromoteurs[0].count) }" />
            </div>
            <span class="hbar__value">{{ format(item.count) }}</span>
          </router-link>
        </div>
      </div>

      <div class="panel">
        <div class="panel__title">
          Répartition des ventes par statut (depuis le début)
        </div>
        <div class="panel__body">
          <router-link
            v-for="status in statusList"
            :key="status.value"
            :to="{ name: 'reservations', query: listQuery(status.value) }"
            class="hbar">
            <span class="hbar__name">
              <span :class="pillClass(RESERVATION_STATUS, status.value)">{{ status.text }}</span>
            </span>
            <div class="rate__bar">
              <div :style="{ width: ratio(totals[status.value], totalAll) }" />
            </div>
            <span class="hbar__value">{{ format(totals[status.value]) }}</span>
            <span class="hbar__percent">{{ percent(totals[status.value], totalAll) }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import RESOURCES from '@batimentb/vadimm-js-api/resources'
  import RemoteSelect from '../components/RemoteSelect'
  import { RESERVATION_STATUS, toOptions, pillClass } from '../labels'

  // Date qui place chaque statut dans la période choisie. Pas de date d'annulation en base :
  // l'annulation écrit updated_on (même repère que la RGPD « annulée depuis »).
  // Les statuts en cours (en attente, pré-réservée) sont comptés à aujourd'hui, sans période.
  const STATUS_DATE = {
    reserved: 'signature_date',
    sold: 'sale_date',
    canceled: 'updated_on'
  }
  // La carte « Réservée » compte toutes les réservations signées sur la période, même vendues depuis :
  // sinon une réservation signée puis actée sortirait du compteur et 24 mois paraîtrait incohérent
  const STATUS_COUNTED = {
    reserved: ['reserved', 'sold']
  }
  const ONGOING_STATUS = ['pending', 'editing', 'pre_reserved']
  // L'API ne renvoie jamais les ventes en édition ni refusées : leur compteur serait toujours à 0
  const HIDDEN_STATUS = ['editing', 'refused']

  export default {
    name: 'Stats',
    components: { RemoteSelect },
    data () {
      return {
        RESERVATION_STATUS,
        accountResource: RESOURCES.ACCOUNT,
        statusList: toOptions(RESERVATION_STATUS).filter(s => !HIDDEN_STATUS.includes(s.value)),
        periods: [
          { key: 'month', label: 'Ce mois', from: () => moment().startOf('month') },
          { key: '3m', label: '3 mois', from: () => moment().subtract(2, 'months').startOf('month') },
          { key: '12m', label: '12 mois', from: () => moment().subtract(11, 'months').startOf('month') },
          { key: '24m', label: '24 mois', from: () => moment().subtract(23, 'months').startOf('month') }
        ],
        period: '12m',
        promoteur: null,
        account: null,
        counts: {},
        totals: {},
        topPromoteurs: null,
        series: [
          // Réservations dont la date de signature tombe dans le mois, qu'elles soient encore réservées ou déjà vendues
          { key: 'signed', title: 'Réservations signées par mois (12 derniers mois)', unit: 'réservation(s) signée(s)', field: 'signature_date', status: ['reserved', 'sold'], months: [] },
          { key: 'sold', title: 'Ventes actées par mois (12 derniers mois)', unit: 'vente(s) actée(s)', field: 'sale_date', status: 'sold', months: [] }
        ]
      }
    },
    computed: {
      // Cartes : vendues avant réservations signées
      cards () {
        const order = ['pending', 'pre_reserved', 'sold', 'reserved', 'canceled']
        return [...this.statusList].sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
      },
      periodLabel () {
        return this.periods.find(p => p.key === this.period).label.toLowerCase()
      },
      totalAll () {
        return this.statusList.reduce((sum, s) => sum + (this.totals[s.value] || 0), 0)
      }
    },
    created () {
      this.fetch()
    },
    methods: {
      setPeriod (key) {
        if (key === this.period) return
        this.period = key
        this.fetch()
      },
      pillClass,
      cardLabel (status) {
        return status.value === 'reserved' ? 'Réservations signées' : status.text
      },
      basisLabel (status) {
        if (STATUS_DATE[status] === 'signature_date') return 'Sur la période, vendues depuis comprises'
        if (STATUS_DATE[status] === 'sale_date') return 'Actées sur la période'
        if (STATUS_DATE[status] === 'updated_on') return 'Annulées sur la période'
        if (ONGOING_STATUS.includes(status)) return 'En cours aujourd\'hui'
        return 'Depuis le début'
      },
      format (value) {
        if (value === undefined) return '…'
        if (value === null) return '—'
        return value.toLocaleString('fr-FR')
      },
      baseParams () {
        const params = { page: 1, per_page: 1 }
        if (this.promoteur) params.promoteur = this.promoteur
        if (this.account) params.account = this.account
        return params
      },
      listQuery (status) {
        const query = { status }
        if (this.promoteur) query.promoteur = this.promoteur
        if (this.account) query.account = this.account
        return query
      },
      async count (params) {
        try {
          const { data } = await this.$api.axios.get(RESOURCES.RESERVATION, { params })
          return data.count
        } catch (e) {
          return null
        }
      },
      fetch () {
        this.fetchStatusCounts()
        this.fetchTotals()
        this.series.forEach(this.fetchMonths)
        this.fetchTopPromoteurs()
      },
      fetchTotals () {
        this.totals = {}
        this.statusList.forEach(async ({ value }) => {
          this.$set(this.totals, value, await this.count({ ...this.baseParams(), status: value }))
        })
      },
      // Ventes actées sur la période pour chaque promoteur, 5 premiers
      async fetchTopPromoteurs () {
        this.topPromoteurs = null
        const from = this.periods.find(p => p.key === this.period).from()
        let promoteurs = []
        try {
          const { data } = await this.$api.axios.get(RESOURCES.ACCOUNT, { params: { account_type: 'promoteur', page: 1, per_page: 100 } })
          promoteurs = data.items || []
        } catch (e) {
          this.topPromoteurs = []
          return
        }
        if (this.promoteur) promoteurs = promoteurs.filter(p => p.pk === this.promoteur)
        const rows = await Promise.all(promoteurs.map(async p => {
          const params = { ...this.baseParams(), promoteur: p.pk, status: 'sold' }
          if (from) params.sale_date__gte = from.toISOString()
          return { pk: p.pk, name: p.name, count: await this.count(params) }
        }))
        this.topPromoteurs = rows.filter(r => r.count).sort((a, b) => b.count - a.count).slice(0, 5)
      },
      ratio (value, max) {
        return `${max ? Math.round((value || 0) / max * 100) : 0}%`
      },
      percent (value, total) {
        if (!total || value === undefined || value === null) return ''
        return `${(value / total * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
      },
      fetchStatusCounts () {
        this.counts = {}
        const from = this.periods.find(p => p.key === this.period).from()
        this.statusList.forEach(async ({ value }) => {
          const params = { ...this.baseParams(), status: STATUS_COUNTED[value] || value }
          const dateField = STATUS_DATE[value]
          if (from && dateField) params[`${dateField}__gte`] = from.toISOString()
          this.$set(this.counts, value, await this.count(params))
        })
      },
      fetchMonths (serie) {
        const months = []
        for (let i = 11; i >= 0; i--) {
          const start = moment().subtract(i, 'months').startOf('month')
          months.push({ key: start.format('YYYY-MM'), label: start.format('MMM YY'), start, count: undefined })
        }
        serie.months = months
        months.forEach(async month => {
          const params = {
            ...this.baseParams(),
            status: serie.status,
            [`${serie.field}__gte`]: month.start.toISOString(),
            [`${serie.field}__lt`]: month.start.clone().add(1, 'month').toISOString()
          }
          month.count = await this.count(params)
        })
      },
      barHeight (months, count) {
        const max = Math.max(1, ...months.map(m => m.count || 0))
        return `${Math.round((count || 0) / max * 100)}%`
      }
    }
  }
</script>

<style scoped>
  .filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
  }
  .kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 12px;
  }
  .kpi {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 16px;
    background: #fafafa;
    border-radius: 16px;
    color: inherit !important;
    text-decoration: none;
  }
  .kpi:hover {
    background: #f4f4f5;
  }
  .kpi__basis {
    font-size: 11.5px;
    color: #7a7a85;
  }
  .kpi strong {
    font-size: 24px;
    font-weight: 700;
  }
  /* La page tient sur un écran : les graphiques se partagent la hauteur restante */
  .charts {
    flex: 1 1 auto !important;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .charts .panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
  .charts .panel__body {
    flex: 1;
    min-height: 0;
    padding-top: 10px;
    padding-bottom: 16px;
  }
  /* Écran étroit : une seule colonne, la page défile */
  @media (max-width: 1100px) {
    .stats.page--fill {
      height: auto;
    }
    .charts {
      grid-template-columns: 1fr;
      grid-template-rows: none;
    }
    .charts .chart {
      height: 200px;
    }
  }
  /* Barres horizontales reprises de la page Signatures Yousign */
  .hbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    color: inherit !important;
    text-decoration: none;
    font-size: 13px;
  }
  .hbar:hover .hbar__name {
    text-decoration: underline;
  }
  .hbar__name {
    width: 200px;
    flex: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }
  .hbar__value {
    width: 56px;
    text-align: right;
    font-weight: 700;
  }
  .hbar__percent {
    width: 52px;
    text-align: right;
    color: #7a7a85;
    font-size: 12px;
  }
  .rate__bar {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: #f1f1f3;
    overflow: hidden;
  }
  .rate__bar div {
    height: 100%;
    border-radius: 3px;
    background: #111113;
  }
  .chart {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    height: 100%;
  }
  .chart__col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    gap: 4px;
  }
  .chart__value {
    font-size: 12px;
    font-weight: 600;
    color: #2b2b31;
    height: 16px;
  }
  .chart__track {
    flex: 1;
    width: 100%;
    max-width: 28px;
    display: flex;
    align-items: flex-end;
  }
  .chart__bar {
    width: 100%;
    min-height: 2px;
    background: rgba(17, 17, 19, 0.12);
    border-radius: 6px 6px 2px 2px;
    transition: height 0.3s, background 0.15s;
  }
  .chart__col:hover .chart__bar,
  .chart__col:last-child .chart__bar {
    background: #111113;
  }
  .chart__label {
    font-size: 11.5px;
    color: #71717a;
    white-space: nowrap;
  }
</style>
